import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { STATUSES } from 'constants/common';
import { DEFAULT_LESSON_LOCALE } from 'constants/lessonLocale';

import { currentCourseSelector } from 'store/course/selectors';
import { currentLessonSelector } from 'store/lesson/selectors';

import { showToast } from 'helpers/showToast';
import { getLessonPreviewPathByCourseType } from 'helpers/getLessonPathByCourseType';
import { getTranslation } from 'helpers/getTranslation';
import { isLessonReadOnly } from 'helpers/isLessonReadOnly';
import {
  LESSON_LOCALIZATION_FIELDS,
  mergeDifferenceLessonAssets,
} from 'helpers/lessonLocalization.helpers';

import { useStatusChange } from 'hooks/useStatusChange';
import { useLocalizedLessonSaveHandlers } from 'hooks/useLocalizedLessonSaveHandlers';

import {
  convertDataToApi,
  getChangedProperties,
  checkQuestionsValidation,
} from './DifferenceLessonPage.helpers';

import DifferenceLessonPage from './DifferenceLessonPage';

const DifferenceLessonPageContainer = (props: any) => {
  const lesson = useAppSelector(currentLessonSelector);

  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [translateAllContentLoading, setTranslateAllContentLoading] =
    useState(false);
  const [lastActionAt, setLastActionAt] = useState(lesson.updatedAt);

  const navigate = useNavigate();

  const { courseId, lessonId } = useParams();

  const course = useAppSelector(currentCourseSelector);

  const [onStatusContextItemClick] = useStatusChange(lesson);

  const {
    activeLocale,
    availableLocales,
    baseLesson,
    editorLesson,
    localeContentLoading,
    localesWithContent,
    mergedLessonLoading,
    onLocaleChange,
    handleAutoSaveSubmit,
    handleSaveClick,
    generateFromLocale,
    referenceLesson,
  } = useLocalizedLessonSaveHandlers({
    lessonId: lessonId as string,
    lessonType: lesson.type,
    baseLesson: lesson,
    convertDataToApi,
    getChangedProperties,
  });

  const handleStatusContextItemClick = (
    contextItem: any,
    values: any,
    { setFieldValue }: { setFieldValue: any },
  ) => {
    if (
      contextItem.key === STATUSES.PUBLISHED &&
      !checkQuestionsValidation(values)
    ) {
      showToast({
        data: {
          variant: 'warning',
          title: getTranslation('common.toast.answer_warning.title'),
          text: getTranslation('common.toast.answer_warning.text'),
        },
      });
      setFieldValue(
        'statusError',
        getTranslation('difference_lesson_page.status_error_text'),
      );

      return;
    }

    onStatusContextItemClick(contextItem, convertDataToApi(values));
  };

  const onAutoSaveSubmit = async (values: any) => {
    setAutoSaveLoading(true);

    const updatedAt = await handleAutoSaveSubmit(values);

    if (updatedAt) {
      setLastActionAt(updatedAt);
    }

    setAutoSaveLoading(false);
  };

  const onSaveClick = async (values: any) => {
    setSaveLoading(true);

    try {
      const updatedAt = await handleSaveClick(values);
      setLastActionAt(updatedAt);

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation('common.toast.lesson_saved.text'),
        },
      });
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.error.text'),
        },
      });
    }

    setSaveLoading(false);
  };

  const handlePreviewClick = () => {
    navigate(
      getLessonPreviewPathByCourseType({
        courseType: course.type,
        courseId,
        lessonId,
      }),
    );
  };

  const handleTranslateAllContentFromRu = async () => {
    if (activeLocale !== 'en') {
      return;
    }

    setTranslateAllContentLoading(true);

    try {
      await generateFromLocale({
        sourceLocale: DEFAULT_LESSON_LOCALE,
        fields: LESSON_LOCALIZATION_FIELDS[lesson.type],
      });

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation('common.toast.translate_all_content_success.text'),
        },
      });
    } catch (error) {
      console.warn(error);

      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation('common.toast.translate_all_content_error.text'),
        },
      });
    } finally {
      setTranslateAllContentLoading(false);
    }
  };

  const lessonReadOnly = isLessonReadOnly(lesson.status);
  const canTranslateAllContentFromRu =
    activeLocale === 'en' &&
    localesWithContent.includes(DEFAULT_LESSON_LOCALE) &&
    !lessonReadOnly;
  const editorLessonWithAssets = useMemo(
    () => mergeDifferenceLessonAssets(referenceLesson, editorLesson),
    [editorLesson, referenceLesson],
  );

  return (
    <DifferenceLessonPage
      {...props}
      saveLoading={saveLoading}
      autoSaveLoading={autoSaveLoading || mergedLessonLoading}
      readOnly={lessonReadOnly}
      baseLesson={baseLesson}
      lesson={editorLessonWithAssets}
      localeContentLoading={localeContentLoading}
      lastActionAt={lastActionAt}
      courseTitle={course.title || getTranslation('common.text.untitled')}
      courseStatus={course.status}
      isCatalogLesson={!courseId}
      activeLocale={activeLocale}
      availableLocales={availableLocales}
      localesWithContent={localesWithContent}
      lessonType={lesson.type}
      onLocaleChange={onLocaleChange}
      onAutoSaveSubmit={onAutoSaveSubmit}
      onSaveClick={onSaveClick}
      onPreviewClick={handlePreviewClick}
      onStatusContextItemClick={handleStatusContextItemClick}
      translateAllContentLoading={translateAllContentLoading}
      canTranslateAllContentFromRu={canTranslateAllContentFromRu}
      onTranslateAllContentFromRu={handleTranslateAllContentFromRu}
    />
  );
};

export default React.memo(DifferenceLessonPageContainer) as any;
